// FC-5 R2/R3：C1-c′／C1-c″ 裁決要求 React 側 texts 必須由「實機互動觸發」取得，不能用靜態字典查表。
// 這支模組是那個「觸發後該顯示什麼」的單一權威來源——production 元件（CrudListPage／
// ComplexFormPage／ReportPage／DisplayPage／DataTableShell）與 Playwright 觸發腳本
// （tests/react/extract-texts.spec.ts）都呼叫**同一支函式**，確保「畫面上真的畫出什麼」
// 與「契約認定畫面應該畫出什麼」永遠是同一個計算結果，不會各自維護一份對照表而漂移。
//
// 【R2 版限制，R3 已解除】R2 版每頁每個分類只有 1 個「通用觸發點」，一次只能顯示 1 條訊息
// （tier 優先序 unique > shared > global 取單一代表），導致同頁同分類多條訊息互相碰撞、
// 60/70 條 untriggered。使用者裁決 R3 改架構：**每頁每個分類可同時顯示多條訊息**——
//   - validation：`resolveAllLegacyMessages()` 回傳這頁這個分類的**全部**訊息，
//     production 元件把它們分配到欄位（逐欄位一條）＋溢出時進一份額外清單，全部同時顯示。
//   - confirm／failure：`resolveLegacyMessageForAction()` 依訊息文字本身的動作關鍵字
//     （刪除／停用／啟用／儲存／匯出／查詢／匯入／推薦）分派——mock DataSource 依
//     resource＋action 決定要顯示哪一條，不同的動作（刪除鈕、啟用/停用切換鈕、儲存鈕）
//     各自觸發各自的訊息，不再共用同一個「通用觸發點」。
// 沒有被分派到、且該頁沒有對應動作可以掛的訊息才是真的「架構不支援」，記進
// `docs/LEGACY-TEXTS-UNTRIGGERED.md` 的 (e) 待辦。

import {
  LEGACY_TEXT_ITEMS,
  LEGACY_TEXTS_BY_PAGE,
  type LegacyTextItem,
  type TextCategory,
} from '@/app/legacy-texts.generated';
import { routes as routeEntries } from '@/app/routes.generated';

const BY_KEY = new Map(LEGACY_TEXT_ITEMS.map((item) => [item.key, item]));

const TIER_RANK: Record<LegacyTextItem['tier'], number> = { unique: 0, shared: 1, global: 2 };

export interface ResolvedLegacyMessage {
  readonly key: string;
  readonly text: string;
}

/**
 * 依 pageId + 分類找出「這個通用觸發點應該顯示哪一條 legacy 訊息」（單一代表，R2 舊行為）。
 * 找不到（這頁這個分類沒有任何 legacy 訊息）回傳 null——呼叫端此時應該
 * 退回原本的雛形通用文案（`t('confirm.delete')` 等），不是留白。
 *
 * 仍保留給「這個分類天生只會有一條」的情境用（例如 success，全站只有 1 條）；
 * 有多條、需要全部顯示或依動作分派的情境改用 `resolveAllLegacyMessages()` /
 * `resolveLegacyMessageForAction()`（見下）。
 */
export function resolveLegacyMessage(
  pageId: string,
  category: TextCategory,
  messageKeyOverride?: string,
): ResolvedLegacyMessage | null {
  const keys = LEGACY_TEXTS_BY_PAGE[pageId] ?? [];
  let best: LegacyTextItem | null = null;
  for (const key of keys) {
    const item = BY_KEY.get(key);
    if (item === undefined || item.catReviewed !== category) continue;
    // FC-5 R4（C1-c‴）：同頁同分類多條訊息碰撞時，注入的 messageKey 若命中這頁這個
    // 分類的候選清單，直接短路回那一條——不命中（不在候選清單裡、或根本沒帶）一律
    // 退回原本的 tier 優先序挑法（R2/R3 行為不變）。
    if (messageKeyOverride !== undefined && item.key === messageKeyOverride) {
      return { key: item.key, text: item.text };
    }
    if (best === null || TIER_RANK[item.tier] < TIER_RANK[best.tier]) best = item;
  }
  return best === null ? null : { key: best.key, text: best.text };
}

/**
 * R3：這頁這個分類的**全部**訊息（依 tier 優先序排列，unique 在前），不再只取一個代表。
 * validation 類用這支——production 元件把回傳陣列分配到各欄位＋溢出清單，全部同時渲染。
 */
export function resolveAllLegacyMessages(
  pageId: string,
  category: TextCategory,
): readonly ResolvedLegacyMessage[] {
  const keys = LEGACY_TEXTS_BY_PAGE[pageId] ?? [];
  const items: LegacyTextItem[] = [];
  for (const key of keys) {
    const item = BY_KEY.get(key);
    if (item === undefined || item.catReviewed !== category) continue;
    items.push(item);
  }
  items.sort((a, b) => TIER_RANK[a.tier] - TIER_RANK[b.tier] || a.key.localeCompare(b.key));
  return items.map((it) => ({ key: it.key, text: it.text }));
}

/**
 * R3：confirm／failure 訊息依「動作關鍵字」分派用的動作集合。
 * 關鍵字直接從訊息文字本身判斷（見 `actionKeywordOf`）——這些動詞是 legacy 原文自帶的，
 * 不是本批發明的分類，例如「是否確定要停用?」的動作顯然是 disable。
 */
export type LegacyActionKeyword =
  | 'delete'
  | 'disable'
  | 'enable'
  | 'save'
  | 'import'
  | 'export'
  | 'query'
  | 'recommend'
  | 'close-dirty'
  | 'dispatch-self';

// FC-6 項目 1（Q2，criteria C5-a）：legacy.shared.06／shared.13 原本落進通用 'save' 桶，
// 敘述變成「crud-list 通用存檔確認」，但兩條實際上是不相干的兩個專屬功能——
// shared.06 是班級點名彈窗的 dirty-close guard（js-my/class/courseModal.js:46/:63），
// shared.13 是表單樣板旁「發送表單給自己」的專屬按鈕（js-my/class/clazzQueryList.js:220，
// 確認框在 WEB-INF/views/course/clazzQueryFormModal.jsp:131）。這兩個關鍵字必須排在
// 一般 patterns 之前，讓它們不再落入 'save' 桶——`compatiblePageTypes()` 對這兩個關鍵字
// 回 []（雛形尚無對應功能），誠實反映「沒有 UI 可以觸發」，不是「跟存檔搞混」。
const SPECIFIC_ACTION_PATTERNS: readonly { keyword: LegacyActionKeyword; pattern: RegExp }[] = [
  { keyword: 'close-dirty', pattern: /尚未存檔.*關閉/ },
  { keyword: 'dispatch-self', pattern: /發表單給自己/ },
];

const ACTION_KEYWORD_PATTERNS: readonly { keyword: LegacyActionKeyword; pattern: RegExp }[] = [
  ...SPECIFIC_ACTION_PATTERNS,
  { keyword: 'disable', pattern: /停用/ },
  { keyword: 'enable', pattern: /啟用/ },
  { keyword: 'delete', pattern: /刪除/ },
  { keyword: 'export', pattern: /下載|匯出/ },
  { keyword: 'import', pattern: /匯入/ },
  { keyword: 'recommend', pattern: /推薦/ },
  { keyword: 'query', pattern: /查詢|查不到|查無|沒有.*名單/ },
];

/** 抓不到關鍵字的一律歸 'save'（儲存／新增／修改／其他寫入動作的通用桶）。 */
export function actionKeywordOf(text: string): LegacyActionKeyword {
  for (const { keyword, pattern } of ACTION_KEYWORD_PATTERNS) {
    if (pattern.test(text)) return keyword;
  }
  return 'save';
}

/**
 * R3：這頁這個分類裡，動作關鍵字符合 `action` 的那一條訊息。
 * 同一頁同分類仍有多條訊息共用同一個關鍵字時（例如兩條都判成 'save'），取第一條
 * （tier 優先序），其餘的那一條在目前的動作分派粒度下確實觸發不到——
 * 誠實記錄成 untriggered，不是這支函式的錯，是分派粒度用完了。
 */
export function resolveLegacyMessageForAction(
  pageId: string,
  category: TextCategory,
  action: LegacyActionKeyword,
  messageKeyOverride?: string,
): ResolvedLegacyMessage | null {
  const all = resolveAllLegacyMessages(pageId, category);
  // FC-5 R4（C1-c‴）：同頁同動作多條訊息碰撞（`.find()` 只會挑第一條）時，注入的
  // messageKey 若命中這頁這個分類的候選清單、且動作關鍵字也真的等於 `action`
  // （不能讓 messageKey 跨動作亂接——例如拿 delete 桶的 key 接到 save 的觸發點），
  // 就顯示那一條；不命中（不在候選清單裡、動作對不上、或根本沒帶）一律退回原本的
  // 「動作關鍵字自動挑選」（R3 行為不變）。
  if (messageKeyOverride !== undefined) {
    const overridden = all.find(
      (item) => item.key === messageKeyOverride && actionKeywordOf(item.text) === action,
    );
    if (overridden !== undefined) return overridden;
  }
  const matched = all.find((item) => actionKeywordOf(item.text) === action);
  if (matched !== undefined) return matched;
  // FC-5 R5（C1-c 修正，findings-R4.md 驗證者 A 最終回報）：**移除**原本「退回 generic
  // save 桶 ?? all[0]」的退路——那條退路會讓語意不相稱的訊息冒出來（例如按「停用」卻跳出
  // 「確定要儲存嗎?」），對 C5-b（假訊息率）是風險：擷取腳本會誤以為這頁這個動作「有訊息
  // 可顯示」而記成已觸發，但畫面上顯示的其實是別的動作的訊息，不是這個動作真正對應的
  // legacy 文字。這頁這個分類沒有 `action` 專屬的訊息時，呼叫端一律拿不到 legacy 訊息
  // （呼叫端已有 `?? t('...')` 的雛形通用文案退路，不會整個變空白）——誠實反映「這個動作
  // 在這頁沒有掛上對應的 legacy 訊息」，不得用別的動作的訊息充數。
  return null;
}

export interface TriggerPlan {
  readonly pageId: string;
  readonly category: TextCategory;
  /** confirm／failure 分派用；其他分類忽略。 */
  readonly action: LegacyActionKeyword;
}

/**
 * 供 Playwright 觸發腳本用：這個 key 在哪一頁的哪個分類、依哪個動作關鍵字會被觸發。
 * R3：只要這個 key 出現在某一頁的 `resolveAllLegacyMessages()` 結果裡就算「有計劃」——
 * 多槽位顯示下不再有「被更高優先序擠掉」這件事，計劃存在不代表 production 元件
 * 這頁這個分類真的有對應的 UI 動作可以觸發到（那件事由擷取腳本實際導頁驗證，
 * 找不到 DOM 節點就記進 untriggered，理由分開寫）。
 */
const PAGE_TYPE_BY_ID = new Map(routeEntries.map((r) => [r.pageId, r.pageType]));

/**
 * 這個分類＋動作，production 模板哪些頁型真的有對應的 UI 可以觸發到。
 * 不是每個頁型都支援每個動作——例如只有 CrudListPage 有「啟用/停用」切換鈕，
 * 只有 ReportPage 有匯出鈕，DisplayPage 沒有刪除也沒有存檔前的確認框。
 */
function compatiblePageTypes(category: TextCategory, action: LegacyActionKeyword): readonly string[] {
  if (category === 'validation') return ['crud-list', 'complex-form', 'report'];
  if (category === 'empty') return ['crud-list', 'report'];
  if (category === 'success') return ['crud-list', 'complex-form', 'display'];
  if (category === 'confirm') {
    // CrudListPage 的新增/修改存檔**沒有**確認框（persist() 直接呼叫，只有刪除跟
    // 狀態切換有 confirm）；只有 ComplexFormPage 的儲存前有確認框
    // （handleSave() 先 dialogs.confirm() 再 persist()）。
    if (action === 'delete' || action === 'disable' || action === 'enable') return ['crud-list'];
    if (action === 'save') return ['complex-form'];
    // 批次 9（2026-09-22，config 7 頁匯入專批）：CrudListPage 的 7 個 config 頁有了匯入
    // 確認框（page-behaviors.tsx CONFIG_IMPORT_SPECS；comment 頁用 t('legacy.unique.35')），
    // 「import 沒有對應 UI」不再是事實——比照下方 export 的修正先例回真實狀態。
    if (action === 'import') return ['crud-list'];
    // FC-6：'close-dirty'（點名彈窗未完成離開確認）／'dispatch-self'（發送表單給自己
    // 專屬按鈕）雛形目前都沒有對應 UI，誠實回 []——不得退回 'save' 的頁型清單，
    // 那正是 Q2 要修的誤判本身。
    if (action === 'close-dirty' || action === 'dispatch-self') return [];
    return [];
  }
  if (category === 'failure') {
    switch (action) {
      case 'delete':
      case 'disable':
      case 'enable':
        return ['crud-list'];
      case 'export':
        // FC-5 R5（C1-c 修正）：CrudListPage 也有匯出鈕與匯出失敗訊息路徑
        // （見 CrudListPage.tsx 的 exportRows()、DataTableShell 的 exportEffect），
        // 之前只填 'report' 是這份分派表的疏漏，不是產品實際限制——
        // legacy.shared.03／legacy.unique.02 因此被誤記進 (e) 名單。
        return ['report', 'crud-list'];
      case 'import':
        // 批次 9（2026-09-22，config 7 頁匯入專批）：7 個 config crud-list 頁有了匯入
        // 失敗路徑（page-behaviors.tsx CONFIG_IMPORT_SPECS；comment 頁用 t('legacy.unique.12')），
        // 誠實回真實狀態（比照上方 export 的 FC-5 R5 修正先例）。
        return ['crud-list'];
      case 'recommend':
        return ['complex-form', 'crud-list'];
      case 'query':
        return ['crud-list', 'complex-form', 'display', 'report'];
      default:
        return ['crud-list', 'complex-form', 'display'];
    }
  }
  return [];
}

/**
 * 供 Playwright 觸發腳本用：這個 key 在哪一頁的哪個分類、依哪個動作關鍵字會被觸發。
 * R3：多槽位顯示下，這個 key 只要出現在 `item.pages` 任何一頁的 `resolveAllLegacyMessages()`
 * 結果裡就「有計劃」——但不是每個指定的頁都真的有對應動作的 UI（見
 * `compatiblePageTypes()`），所以優先在 `item.pages` 裡挑一個**頁型相容**的頁；
 * 找不到相容的頁才退回第一個（讓擷取腳本自己導頁觀察不到，誠實記錄理由，
 * 不是這支函式在說謊）。
 */
export function whereWouldBeTriggered(itemKey: string): TriggerPlan | null {
  const item = BY_KEY.get(itemKey);
  if (item === undefined) return null;
  const action = actionKeywordOf(item.text);
  const compatible = compatiblePageTypes(item.catReviewed, action);
  const compatiblePage = item.pages.find((p) => compatible.includes(PAGE_TYPE_BY_ID.get(p) ?? ''));
  const pageId = compatiblePage ?? item.pages[0];
  if (pageId === undefined) return null;
  return { pageId, category: item.catReviewed, action };
}
